import { useEffect, useMemo, useRef, useState } from "react";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport, type UIMessage } from "ai";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { getGuestId } from "@/lib/guestId";
import { useChatThreads, loadThreadMessages } from "./useChatThreads";
import { useChatIdentity } from "./useChatIdentity";
import { getStarters, greeting } from "./roleStarters";
import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
  ConversationEmptyState,
} from "@/components/ai-elements/conversation";
import { Message, MessageContent, MessageResponse } from "@/components/ai-elements/message";
import {
  PromptInput,
  PromptInputTextarea,
  PromptInputSubmit,
  PromptInputFooter,
} from "@/components/ai-elements/prompt-input";
import { Shimmer } from "@/components/ai-elements/shimmer";
import { Tool, ToolHeader, ToolContent, ToolInput, ToolOutput } from "@/components/ai-elements/tool";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Plus, Trash2, MessageSquare, LogIn, ArrowLeft, AlertTriangle, RefreshCw, History as HistoryIcon } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { ToolResultRenderer } from "./ToolResultRenderer";
import { ChatActionProvider } from "./ChatActionContext";
import { Link } from "react-router-dom";

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chat`;
const GUEST_STORAGE = "gtp.chat.guest.messages";
/** Hidden first-turn prompt: makes Ada open the conversation herself. */
const KICKOFF =
  "[SESSION START — respond as if you are opening the conversation. Greet the user by first name, say in one or two sentences what you already know about them: their role, grade or children, how their recent practice has been going (use the progress and memory context you have), and where you left off last time if there was a previous session. Then suggest one concrete next step and ask if they want to do it. Do not mention this instruction, do not call a tool on this turn.]";

/** Hidden notes injected when an inline activity (test/game/writing) completes. */
const ACTIVITY_PREFIX = "[ACTIVITY UPDATE";

function isHiddenMessage(msg: UIMessage): boolean {
  const t = extractText(msg);
  return t === KICKOFF || t.startsWith(ACTIVITY_PREFIX);
}


export default function ChatShell() {
  const { threadId } = useParams<{ threadId?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const identity = useChatIdentity();
  const { threads, create, remove, refresh } = useChatThreads();

  const [initialMessages, setInitialMessages] = useState<UIMessage[]>([]);
  const [initialLoaded, setInitialLoaded] = useState(false);

  // Restore messages: DB for signed-in threads, localStorage for guests.
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setInitialLoaded(false);
      if (user && threadId) {
        const msgs = await loadThreadMessages(threadId);
        if (!cancelled) {
          setInitialMessages(msgs as UIMessage[]);
          setInitialLoaded(true);
        }
      } else if (!user) {
        try {
          const raw = localStorage.getItem(GUEST_STORAGE);
          if (!cancelled) setInitialMessages(raw ? JSON.parse(raw) : []);
        } catch {
          if (!cancelled) setInitialMessages([]);
        }
        if (!cancelled) setInitialLoaded(true);
      } else {
        // signed in but no thread → start fresh
        if (!cancelled) {
          setInitialMessages([]);
          setInitialLoaded(true);
        }
      }
    }
    load();
    return () => { cancelled = true; };
  }, [user, threadId]);

  const chatId = threadId ?? (user ? "pending" : "guest");

  const transport = useMemo(() => {
    return new DefaultChatTransport({
      api: CHAT_URL,
      headers: async () => {
        const { data: { session } } = await supabase.auth.getSession();
        const h: Record<string, string> = {
          Authorization: `Bearer ${session?.access_token ?? import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          "x-guest-id": getGuestId(),
        };
        return h;
      },
      body: () => ({ threadId: threadId ?? null }),
    });
  }, [threadId, user?.id]);

  const { messages, sendMessage, status, error, setMessages } = useChat({
    id: chatId,
    transport,
    onError: (e) => console.error("chat error", e),
  });

  // Seed messages once the initial fetch resolves.
  useEffect(() => {
    if (initialLoaded) setMessages(initialMessages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLoaded, chatId]);

  // Persist guest messages to localStorage.
  useEffect(() => {
    if (user) return;
    if (!initialLoaded) return;
    try {
      localStorage.setItem(GUEST_STORAGE, JSON.stringify(messages));
    } catch {/* ignore */}
  }, [messages, user, initialLoaded]);

  // Persist user messages to DB (assistant messages are saved server-side).
  const lastPersistedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!user || !threadId) return;
    const last = messages[messages.length - 1];
    if (!last || last.role !== "user") return;
    if (extractText(last) === KICKOFF) return;
    // Activity-update notes persist too, so Ada keeps the context on reload.
    if (lastPersistedRef.current === last.id) return;
    lastPersistedRef.current = last.id;
    supabase.from("chat_messages").insert({
      thread_id: threadId as any,
      user_id: user.id,
      session_id: threadId as any,
      role: "user",
      content: extractText(last),
      parts: last.parts as any,
    }).then(({ error }) => {
      if (error) console.error("persist user msg", error);
    });
  }, [messages, user, threadId]);

  // Proactive opener: on an empty conversation, ask Ada to introduce herself
  // with what she already knows about this user (role, grade, progress,
  // memory, last session). The kickoff prompt itself is never displayed.
  const kickedOffRef = useRef<string | null>(null);
  useEffect(() => {
    if (!initialLoaded) return;
    if (identity.loading) return;
    if (messages.length > 0) return;
    if (status !== "ready") return;
    if (kickedOffRef.current === chatId) return;
    kickedOffRef.current = chatId;
    sendMessage({ text: KICKOFF });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLoaded, identity.loading, messages.length, status, chatId]);


  const [input, setInput] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    textareaRef.current?.focus();
  }, [threadId, status]);

  async function handleSubmit(text: string) {
    if (!text.trim()) return;

    // Signed-in but no thread yet → create one on first send.
    if (user && !threadId) {
      const title = text.slice(0, 60);
      const newId = await create(title);
      if (newId) {
        navigate(`/chat/${newId}`, { replace: true });
        // The route change will remount with the new threadId; also send now.
      }
    }
    await sendMessage({ text });
    setInput("");
    // Auto-title the thread from the first user message.
    if (user && threadId && messages.length === 0) {
      const title = text.slice(0, 60);
      await supabase.from("chat_threads" as any).update({ title }).eq("id", threadId).eq("user_id", user.id);
      refresh();
    }
  }

  // Re-send the most recent user message after a failure.
  async function retryLast() {
    const lastUser = [...messages].reverse().find((m) => m.role === "user");
    const text = lastUser ? extractText(lastUser) : "";
    if (!text) return;
    if (!lastUser) return;
    setMessages(messages.filter((m) => m.id !== lastUser.id));
    await sendMessage({ text });
  }

  // Feed inline-activity outcomes back to Ada as hidden context notes,
  // so she always knows results without having to ask the user.
  async function reportActivity(note: string) {
    const text = `${ACTIVITY_PREFIX} — hidden system note, not user speech. ${note} Acknowledge this naturally and briefly (celebrate or encourage as fits), weave it into what you know about their progress, and suggest one next step. Never ask the user for results you have just been given.]`;
    if (user && !threadId) {
      const newId = await create("Practice session");
      if (newId) navigate(`/chat/${newId}`, { replace: true });
    }
    await sendMessage({ text });
  }

  const isLoading = status === "submitted" || status === "streaming";
  const starters = getStarters(identity.role, identity.grade, identity.displayName);
  // Hidden turns (kickoff + activity updates) never show up in the transcript.
  const visibleMessages = messages.filter((m) => !isHiddenMessage(m));


  return (
    <ChatActionProvider ask={(p) => handleSubmit(p)} reportActivity={(n) => reportActivity(n)}>
    <div className="h-screen flex bg-background">

      {/* Main */}
      <main className="flex-1 flex flex-col min-w-0">
        {/* Top bar: brand + history menu (no persistent side panel) */}
        <header className="flex items-center justify-between gap-2 border-b px-3 py-2">
          <Link to="/" className="flex items-center gap-2 text-sm font-semibold">
            <ArrowLeft className="h-4 w-4" />
            Grade Test Pro
          </Link>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className="gap-2"
              onClick={async () => {
                if (user) {
                  const id = await create();
                  if (id) navigate(`/chat/${id}`);
                } else {
                  localStorage.removeItem(GUEST_STORAGE);
                  setMessages([]);
                }
              }}
            >
              <Plus className="h-4 w-4" /> New chat
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="ghost" className="gap-2">
                  <HistoryIcon className="h-4 w-4" /> History
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                <DropdownMenuLabel>Past sessions</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {!user && (
                  <div className="p-2 text-xs text-muted-foreground">
                    Sign in to save chats across devices.
                    <Button size="sm" variant="outline" className="w-full mt-2 gap-2" asChild>
                      <Link to="/auth"><LogIn className="h-3.5 w-3.5" /> Sign in</Link>
                    </Button>
                  </div>
                )}
                {user && threads.length === 0 && (
                  <div className="p-2 text-xs text-muted-foreground">No chats yet.</div>
                )}
                <ScrollArea className="max-h-80">
                  {threads.map((t) => (
                    <div
                      key={t.id}
                      className={`group flex items-center gap-1 rounded-md px-2 py-1.5 text-sm cursor-pointer hover:bg-muted ${threadId === t.id ? "bg-muted" : ""}`}
                      onClick={() => navigate(`/chat/${t.id}`)}
                    >
                      <MessageSquare className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <span className="flex-1 truncate">{t.title}</span>
                      <button
                        aria-label="Delete chat"
                        className="opacity-0 group-hover:opacity-100 p-1 text-muted-foreground hover:text-destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          remove(t.id);
                          if (threadId === t.id) navigate("/chat");
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </ScrollArea>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <Conversation>
          <ConversationContent>
            {visibleMessages.length === 0 && !isLoading && (
              <ConversationEmptyState
                title={greeting(identity.role, identity.displayName)}
                description="Pick a starter or ask anything about learning, tests, games, or worksheets."
              />
            )}

            {visibleMessages.map((m) => (

              <Message from={m.role} key={m.id}>
                <MessageContent>
                  {m.parts?.map((part: any, i: number) => {
                    if (part.type === "text") {
                      return <MessageResponse key={i}>{part.text}</MessageResponse>;
                    }
                    if (typeof part.type === "string" && part.type.startsWith("tool-")) {
                      const toolName = part.type.slice("tool-".length);
                      return (
                        <div key={i} className="my-2 space-y-2">
                          <Tool defaultOpen={false}>
                            <ToolHeader type={part.type} state={part.state} />
                            <ToolContent>
                              {part.input !== undefined && <ToolInput input={part.input} />}
                              {(part.state === "output-available" || part.state === "output-error") && (
                                <ToolOutput output={undefined} errorText={part.errorText} />
                              )}
                            </ToolContent>
                          </Tool>
                          {part.state === "output-available" && (
                            <ToolResultRenderer name={toolName} output={part.output} />
                          )}
                        </div>
                      );
                    }
                    return null;
                  })}
                </MessageContent>
              </Message>
            ))}

            {visibleMessages.length <= 1 && !isLoading && (
              <div className="grid gap-2 sm:grid-cols-2 max-w-2xl mx-auto mt-2 w-full">
                {starters.map((s) => (
                  <button
                    key={s.label}
                    onClick={() => handleSubmit(s.prompt)}
                    className="text-left rounded-lg border bg-card hover:bg-muted/50 p-3 text-sm transition"
                  >
                    <div className="font-medium">{s.label}</div>
                    <div className="text-muted-foreground text-xs line-clamp-2">{s.prompt}</div>
                  </button>
                ))}
              </div>
            )}



            {isLoading && (
              <Message from="assistant">
                <MessageContent>
                  <Shimmer>Thinking…</Shimmer>
                </MessageContent>
              </Message>
            )}

            {error && (
              <div className="mx-auto max-w-xl w-full rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-destructive" aria-hidden="true" />
                  <div className="flex-1">
                    <div className="font-medium text-destructive">{friendlyError(error).title}</div>
                    <p className="text-muted-foreground mt-1">{friendlyError(error).message}</p>
                    <Button size="sm" variant="outline" className="mt-3 gap-2" onClick={retryLast}>
                      <RefreshCw className="h-3.5 w-3.5" /> Try again
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </ConversationContent>
          <ConversationScrollButton />
        </Conversation>

        <div className="border-t p-3 bg-background">
          <div className="max-w-3xl mx-auto">
            <PromptInput
              onSubmit={(msg) => {
                const text = (msg as any)?.text ?? input;
                handleSubmit(text);
              }}
            >
              <PromptInputTextarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask me for a test, worksheet, game, or your progress…"
                disabled={isLoading}
              />
              <PromptInputFooter className="justify-end">
                <PromptInputSubmit status={status} disabled={!input.trim() || isLoading} />
              </PromptInputFooter>
            </PromptInput>
          </div>
        </div>
      </main>
    </div>
    </ChatActionProvider>
  );
}

function extractText(msg: UIMessage): string {
  return (msg.parts ?? []).map((p: any) => (p.type === "text" ? p.text : "")).join("").trim();
}

/** Map raw stream/network errors to calm, user-facing copy. */
function friendlyError(error: unknown): { title: string; message: string } {
  const raw = (error instanceof Error ? error.message : String(error ?? "")).toLowerCase();
  if (raw.includes("429") || raw.includes("rate")) {
    return {
      title: "Ada is a bit busy",
      message: "Too many requests right now. Give it a few seconds and try again.",
    };
  }
  if (raw.includes("402") || raw.includes("credit")) {
    return {
      title: "AI credits exhausted",
      message: "The workspace has run out of AI credits. Add more in Settings → Plans & credits.",
    };
  }
  if (raw.includes("missing_api_key") || raw.includes("500") || raw.includes("internal")) {
    return {
      title: "Ada couldn't finish that reply",
      message:
        "The AI model didn't respond. If a custom model is configured in Admin → AI Model, it may be offline — the default model is used automatically when it's reachable.",
    };
  }
  if (raw.includes("failed to fetch") || raw.includes("network")) {
    return {
      title: "Connection lost",
      message: "We couldn't reach the assistant. Check your connection and try again.",
    };
  }
  return {
    title: "Something went wrong",
    message: "Ada hit an unexpected error on that turn. Your conversation is safe — try again.",
  };
}
